import React from 'react';
import { motion } from 'framer-motion';
import StatusRing from '../components/StatusRing';
import { frameworks, recentActivity } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { AuditCockpitIllustration } from '../components/VisualIllustrations';
import { FiTrendingUp, FiAlertCircle, FiClock, FiCheckCircle, FiDatabase, FiInfo, FiShield } from 'react-icons/fi';

const Dashboard = () => {
  const chartData = [
    { value: 45, name: 'Compliant', itemStyle: { color: '#10B981' } },
    { value: 25, name: 'In Progress', itemStyle: { color: '#3B82F6' } },
    { value: 15, name: 'Reviewing', itemStyle: { color: '#F59E0B' } },
    { value: 15, name: 'Gap Identified', itemStyle: { color: '#EF4444' } }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* Overview Section */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-slate-900 via-slate-900 to-[#0f172a] border border-slate-800 rounded-2xl p-8 shadow-2xl relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 p-12 opacity-5 scale-150 rotate-12 group-hover:rotate-6 transition-transform duration-1000">
          <SafeIcon icon={FiShield} className="text-9xl text-amber-500" />
        </div>
        
        <div className="relative z-[1] flex flex-col lg:flex-row gap-8 items-center">
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-500 px-3 py-1 rounded-full border border-amber-500/20 text-[10px] font-bold uppercase tracking-widest mb-4">
              <SafeIcon icon={FiShield} /> Secure Audit Cockpit
            </div>
            <h1 className="text-4xl font-extrabold text-slate-100 tracking-tight">
              Master Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Compliance Flow</span>
            </h1>
            <p className="text-slate-400 mt-4 max-w-2xl text-lg leading-relaxed">
              Replace fragmented spreadsheets with a unified command center. Track readiness, harvest evidence, and bridge gaps with an intuitive, organizational-first platform built for modern security teams.
            </p>
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4">
              <button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-3 rounded-xl transition-all shadow-[0_0_20px_rgba(245,158,11,0.2)]">
                Launch Audit Window
              </button>
              <button className="bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold px-6 py-3 rounded-xl border border-slate-700 transition-all">
                Export Readiness Report
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 w-full lg:w-auto">
            <div className="bg-slate-950/50 backdrop-blur-xl p-6 rounded-2xl border border-slate-800 text-center min-w-[160px] shadow-xl group/card">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest mb-1 group-hover/card:text-emerald-400 transition-colors">Global Posture</p>
              <p className="text-4xl font-black text-emerald-400">84%</p>
              <div className="w-full h-1 bg-slate-800 rounded-full mt-4 overflow-hidden">
                <div className="h-full bg-emerald-500 w-[84%] shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
              </div>
            </div>
            <div className="bg-slate-950/50 backdrop-blur-xl p-6 rounded-2xl border border-slate-800 text-center min-w-[160px] shadow-xl group/card">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest mb-1 group-hover/card:text-red-400 transition-colors">Risk Gaps</p>
              <p className="text-4xl font-black text-red-400">12</p>
              <p className="text-[10px] text-slate-600 mt-2 font-medium italic">Requires attention</p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: 'Total Frameworks', value: '4', icon: FiDatabase, color: 'text-blue-400', bg: 'bg-blue-400/10' },
          { title: 'Controls Met', value: '84%', icon: FiCheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
          { title: 'Open Gaps', value: '12', icon: FiAlertCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
          { title: 'Days to Audit', value: '25', icon: FiClock, color: 'text-amber-400', bg: 'bg-amber-400/10' },
        ].map((stat, i) => (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            key={i} 
            className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-6 backdrop-blur-md hover:border-slate-700 transition-all group"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">{stat.title}</p>
                <h3 className="text-3xl font-black text-slate-100 mt-1">{stat.value}</h3>
              </div>
              <div className={`p-3 rounded-xl ${stat.bg} ${stat.color} group-hover:scale-110 transition-transform`}>
                <SafeIcon icon={stat.icon} className="text-xl" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-6 backdrop-blur-md shadow-xl">
            <h2 className="text-lg font-bold text-slate-200 mb-6 flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="text-amber-400" />
              Framework Readiness Trends
            </h2>
            <AuditCockpitIllustration />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {frameworks.map((fw) => (
                <div key={fw.id} className="p-4 bg-slate-950/40 border border-slate-800 rounded-xl group hover:border-amber-500/30 transition-all">
                  <div className="flex justify-between text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-200">{fw.name}</span>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter ${fw.status === 'At Risk' ? 'bg-red-500/10 text-red-500' : 'bg-slate-800 text-slate-500'}`}>
                        {fw.status}
                      </span>
                    </div>
                    <span className="text-slate-400 font-mono">{fw.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${fw.progress}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-full rounded-full ${fw.progress > 80 ? 'bg-emerald-500' : fw.progress > 40 ? 'bg-amber-500' : 'bg-red-500'} shadow-[0_0_10px_rgba(currentColor,0.4)]`} 
                    ></motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-8 backdrop-blur-md flex flex-col justify-center items-center relative min-h-[350px] shadow-xl overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-30" />
            <StatusRing data={chartData} title="Global Posture" />
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500 leading-relaxed font-medium uppercase tracking-widest mb-2">Audit Readiness Score</p>
              <div className="inline-flex gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                <span className="text-emerald-400 font-bold">Optimized for Q4</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-2xl p-6 shadow-lg">
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-amber-500 text-slate-950 rounded-xl shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                <SafeIcon icon={FiInfo} className="text-xl" />
              </div>
              <div>
                <h4 className="text-sm font-black text-amber-500 uppercase tracking-widest">Audit Alert</h4>
                <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                  Your SOC 2 Type II audit window begins in <b>14 days</b>. We recommend a final sweep of the Evidence Vault today.
                </p>
                <button className="mt-4 text-[10px] font-bold uppercase tracking-widest text-amber-500 hover:text-amber-400 transition-colors flex items-center gap-1 group">
                  Start Sweep <SafeIcon icon={FiTrendingUp} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;